import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { PageUIModel, TopicLevel1Query, TopicLevel2Query } from '@examdojo/category/v2';
import { IconComponent } from '@examdojo/core/icon';

@Component({
  selector: 'dojo-topic-breadcrumbs',
  imports: [IconComponent, MatTooltipModule],
  template: `
    @if (state.topicLevel1) {
      <div class="flex min-w-0 items-center gap-2 text-sm text-neutral-400">
        <span
          #tl1
          class="breadcrumb-item"
          [matTooltip]="state.topicLevel1.name"
          [matTooltipDisabled]="!isTextTruncated(tl1)"
        >
          {{ state.topicLevel1.name }}
        </span>
        @if (state.topicLevel2) {
          <dojo-icon icon="chevron-right" class="shrink-0" />
          <span
            #tl2
            class="breadcrumb-item"
            [matTooltip]="state.topicLevel2.name"
            [matTooltipDisabled]="!isTextTruncated(tl2)"
          >
            {{ state.topicLevel2.name }}
          </span>
        }
        @if (showPageBreadcrumb() && activePage()) {
          <dojo-icon icon="chevron-right" class="shrink-0" />
          <span
            #page
            class="breadcrumb-item"
            [matTooltip]="activePage()!.title"
            [matTooltipDisabled]="!isTextTruncated(page)"
          >
            {{ activePage()!.title }}
          </span>
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        container-type: inline-size;
      }

      .breadcrumb-item {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      @container (min-width: 500px) {
        .breadcrumb-item {
          overflow: visible;
          text-overflow: clip;
          min-width: fit-content;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopicBreadcrumbsComponent {
  constructor(
    private readonly topicLevel1Query: TopicLevel1Query,
    private readonly topicLevel2Query: TopicLevel2Query,
  ) {}

  /** The currently active page (passed from parent) */
  readonly activePage = input<PageUIModel | undefined>();

  /** Whether to show the page breadcrumb (when there are multiple pages) */
  readonly showPageBreadcrumb = input<boolean>(false);

  readonly state = connectState({
    topicLevel1: this.topicLevel1Query.active$,
    topicLevel2: this.topicLevel2Query.active$,
  });

  /**
   * Checks if an element's text is truncated (overflow hidden with ellipsis).
   */
  isTextTruncated(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }
}
